/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\NtpServersBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the system module.
----------------------------------------------------------------*/


import React from 'react';
import { Globe } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import type { SystemConfig } from '../../../types/system';

interface NtpServersBoxProps {
  formData: SystemConfig;
  onInputChange: (field: keyof SystemConfig, value: SystemConfig[keyof SystemConfig]) => void;
}

/** Default NTP server pool addresses */
const NTP_PLACEHOLDERS = [
  '0.debian.pool.ntp.org',
  '1.debian.pool.ntp.org',
  '2.debian.pool.ntp.org',
  '3.debian.pool.ntp.org',
] as const;

/**
 * NtpServersBox
 *
 * Renders the NTP Servers FormBox: an enable/disable toggle and four NTP
 * server address inputs (shown only when NTP is enabled).
 *
 * Editing a single server entry replaces only that index in the ntpServers
 * array, keeping the other entries intact.
 */
export function NtpServersBox({ formData, onInputChange }: NtpServersBoxProps) {
  const { t } = useI18n();

  /** Updates a single server at the given index in the ntpServers array */
  const handleServerChange = (index: number, value: string) => {
    const updated = [...formData.ntpServers];
    updated[index] = value;
    onInputChange('ntpServers', updated);
  };

  return (
    <FormBox title="NTP Servers" icon={<Globe />}>
      <div className="space-y-2">
        {/* Enable / Disable NTP */}
        <div className="flex items-center justify-between">
          <SimpleTooltip title="NTP" content={t('systemMisc.tooltips.ntpToggle')} position="top" maxWidth="300px">
            <Label className="text-sm font-medium text-foreground">NTP</Label>
          </SimpleTooltip>
          <Toggle
            value={formData.ntpEnabled}
            onChange={(value) => onInputChange('ntpEnabled', value)}
          />
        </div>

        {/* Server address inputs � shown only when NTP is active */}
        {formData.ntpEnabled && (
          <div className="space-y-3">
            {NTP_PLACEHOLDERS.map((placeholder, index) => (
              <div key={index} className="grid grid-cols-[120px_1fr] gap-3 items-center">
                <SimpleTooltip
                  title={`Server ${index + 1}`}
                  content={t(`systemMisc.tooltips.ntpServer${index + 1}`)}
                  position="top"
                  maxWidth="300px"
                >
                  <Label htmlFor={`ntpServer${index + 1}`} className="text-sm font-medium text-foreground">
                    Server {index + 1}
                  </Label>
                </SimpleTooltip>
                <input
                  id={`ntpServer${index + 1}`}
                  type="text"
                  value={formData.ntpServers[index] ?? ''}
                  onChange={(e) => handleServerChange(index, e.target.value)}
                  className="px-3 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full"
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </FormBox>
  );
}
